import {
  DemarrageData,
  DemarrageStep,
} from '@teetsh/app/src/containers/Demarrage/types';

function isUserInACountryWhereWeHaveSchoolsData(userCountry: string) {
  const countriesWithSchoolDatas = [
    'FR',
    'RE', // Réunion
    'YT', // Mayotte
    'NC', // Nouvelle-Calédonie
    'PF', // Polynésie française
    'GY', // Guyane
    'GP', // Guadeloupe
    'MQ', // Martinique
    'GF', // Guyane
    'WF', // Wallis et Futuna
    'PM', // Saint-Pierre-et-Miquelon
    'US',
    'AU',
  ];
  const uppercaseUserCountry = userCountry.toUpperCase();
  return countriesWithSchoolDatas.includes(uppercaseUserCountry);
}

function computeNextClassesStep(
  demarrageData: DemarrageData,
  etablissementId: string | undefined,
  isEDTTemplate: boolean,
  previousStep: DemarrageStep | undefined,
): {
  nextStep?: DemarrageStep;
  previousStep?: DemarrageStep;
} {
  let nextStep: DemarrageStep = 'jours-travailles';
  if ((demarrageData.degreesWithWorkingDays || []).length > 1) {
    nextStep = 'organisation-multi-niveaux';
  }
  if (isEDTTemplate) {
    return { nextStep: 'objectifs', previousStep };
  }
  if (!etablissementId) {
    return { nextStep, previousStep: 'ecole-details' };
  }
  return { nextStep, previousStep: 'ecole' };
}

const stepCount = 6;

function toProgressionPercent(step: number) {
  return (step / stepCount) * 100;
}

export function computeNextStep(
  currentStep: DemarrageStep,
  demarrageData: DemarrageData,
  userCountry: string,
  isAddTeacherMode?: boolean,
): {
  nextStep?: DemarrageStep;
  previousStep?: DemarrageStep;
  progression?: number;
} {
  const {
    etablissementUai,
    etablissementNcesSchoolId,
    etablissementACARASchoolId,
    edtTemplateId,
    edtTemplateDocumentId,
    sharedEDTId,
  } = demarrageData;
  const etablissementId =
    etablissementUai || etablissementNcesSchoolId || etablissementACARASchoolId;
  const isEDTTemplate =
    !!edtTemplateId || !!edtTemplateDocumentId || !!sharedEDTId;
  const { userRoles } = demarrageData;

  if (currentStep === 'bienvenue') {
    return { nextStep: 'pays' };
  }
  if (currentStep === 'pays') {
    return {
      nextStep: 'role',
      previousStep: 'bienvenue',
      progression: toProgressionPercent(0.5),
    };
  }
  if (currentStep === 'role') {
    const previousStep = 'pays';
    const progression = toProgressionPercent(1);
    if (userRoles?.includes('directeur')) {
      return { nextStep: 'directeur-access', previousStep, progression };
    }
    if (!isUserInACountryWhereWeHaveSchoolsData(userCountry)) {
      return { nextStep: 'ecole-details', previousStep, progression };
    }
    return { nextStep: 'ecole', previousStep, progression };
  }
  if (currentStep === 'directeur-access') {
    const progression = toProgressionPercent(1.5);

    if (!demarrageData?.roles?.includes('teacher')) {
      return { nextStep: 'preparation', previousStep: 'role', progression };
    }

    if (!isUserInACountryWhereWeHaveSchoolsData(userCountry)) {
      return { nextStep: 'ecole-details', previousStep: 'role', progression };
    }
    return { nextStep: 'ecole', previousStep: 'role', progression };
  }
  if (currentStep === 'ecole') {
    let previousStep: DemarrageStep | undefined = 'role';
    const progression = toProgressionPercent(2);
    if (isAddTeacherMode) {
      previousStep = undefined;
    }
    if (!etablissementId) {
      return { nextStep: 'ecole-details', previousStep, progression };
    }
    if (isEDTTemplate) {
      return {
        ...computeNextClassesStep(
          demarrageData,
          etablissementId,
          isEDTTemplate,
          previousStep,
        ),
        progression,
      };
    }
    return { nextStep: 'classes', previousStep, progression };
  }
  if (currentStep === 'ecole-details') {
    let nextStep: DemarrageStep = 'classes';
    if (isEDTTemplate) {
      nextStep = 'objectifs';
    }
    const progression = toProgressionPercent(2);

    if (!isUserInACountryWhereWeHaveSchoolsData(userCountry)) {
      return { nextStep, previousStep: 'role', progression };
    }
    return { nextStep, previousStep: 'ecole', progression };
  }

  if (currentStep === 'classes') {
    let previousStep: DemarrageStep = 'ecole';
    if (!etablissementId) {
      previousStep = 'ecole-details';
    }
    const progression = toProgressionPercent(3);

    return {
      ...computeNextClassesStep(
        demarrageData,
        etablissementId,
        isEDTTemplate,
        previousStep,
      ),
      progression,
    };
  }
  if (currentStep === 'organisation-multi-niveaux') {
    return {
      nextStep: 'jours-travailles',
      previousStep: 'classes',
      progression: toProgressionPercent(3.5),
    };
  }
  if (currentStep === 'jours-travailles') {
    let previousStep: DemarrageStep = 'classes';
    if ((demarrageData.degreesWithWorkingDays || []).length > 1) {
      previousStep = 'organisation-multi-niveaux';
    }
    return {
      nextStep: 'objectifs',
      previousStep,
      progression: toProgressionPercent(4),
    };
  }
  if (currentStep === 'objectifs') {
    const progression = toProgressionPercent(5);
    if (isEDTTemplate) {
      return { nextStep: 'preparation', previousStep: 'ecole', progression };
    }
    return {
      nextStep: 'preparation',
      previousStep: 'jours-travailles',
      progression,
    };
  }
  if (currentStep === 'preparation') {
    return { previousStep: 'objectifs' };
  }
  return {};
}
